import React, { useState, useEffect } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { Button } from 'react-bootstrap';
import { resolverUrlArchivo } from '../../utils/resolverUrlArchivo';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { markdownLigeroAHtml } from '../../utils/richText';
import useEnlaceExterno from '../../utils/useEnlaceExterno';

// Grilla consciente del ancho del contenedor.
const ReactGridLayout = WidthProvider(GridLayout);

// Normaliza canvas_data por si llegara como string JSON.
function normalizar(canvasData) {
  if (!canvasData) return null;
  if (typeof canvasData === 'string') {
    try {
      return JSON.parse(canvasData);
    } catch {
      return null;
    }
  }
  return typeof canvasData === 'object' ? canvasData : null;
}

// Render de un bloque individual (solo lectura). Nunca usa dangerouslySetInnerHTML.
function BloqueVista({ bloque, onClickEnlace }) {
  if (!bloque || typeof bloque !== 'object') return null;
  const { type, props = {} } = bloque;

  switch (type) {
    case 'heading':
      return <h2 className="h4 fw-bold text-danger mb-0">{props.text || ''}</h2>;

    case 'text':
  return (
        <div
          onClick={onClickEnlace}
          dangerouslySetInnerHTML={{ __html: markdownLigeroAHtml(props.text || '') }}
        />
      );

    case 'button': {
      const url = props.href ? resolverUrlArchivo(props.href) : null;
      if (!url) return null;
      return (
        <Button
          variant={props.variant || 'primary'}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {props.label || 'Ver'}
        </Button>
      );
    }

    case 'image': {
      const src = props.src ? resolverUrlArchivo(props.src) : null;
      if (!src) return null;
      return (
        <img
          src={src}
          alt={props.alt || ''}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      );
    }

    case 'document': {
      const url = props.url ? resolverUrlArchivo(props.url) : null;
      if (!url) return null;
      return (
        <Button variant="outline-primary" href={url} target="_blank" rel="noopener noreferrer">
          <i className="bi bi-file-earmark-text me-1" aria-hidden="true"></i>
          {props.titulo || 'Documento'}
        </Button>
      );
    }

    default:
      return null;
  }
}

/**
 * CanvasRenderer — pinta un canvas_data en modo lectura, respetando el layout.
 * En desktop usa la grilla (react-grid-layout). En móvil apila los bloques en
 * una sola columna con altura automática, para que el texto largo no se corte.
 * Robusto: ignora bloques sin layout, layout sin bloque, o datos malformados.
 */
export default function CanvasRenderer({ canvasData }) {
  const [esMovil, setEsMovil] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
   const onClickEnlace = useEnlaceExterno();

  useEffect(() => {
    const onResize = () => setEsMovil(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cd = normalizar(canvasData);
  if (!cd) return null;

  const blocks = cd.blocks && typeof cd.blocks === 'object' ? cd.blocks : {};
  const layoutRaw = Array.isArray(cd.layout) ? cd.layout : [];

  // Solo items válidos con bloque existente.
  const layout = layoutRaw.filter((it) => it && it.i && blocks[it.i]);
  if (layout.length === 0) return null;

  // ── MÓVIL: bloques apilados en columna, altura automática ──
  if (esMovil) {
    // Ordena por posición vertical (y) y luego horizontal (x) para respetar
    // el orden visual que el admin diseñó en el editor.
    const ordenados = [...layout].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });
    return (
      <div className="faq-canvas-movil">
        {ordenados.map((it) => (
          <div key={it.i} className="faq-bloque-movil mb-3">
            <BloqueVista bloque={blocks[it.i]} onClickEnlace={onClickEnlace} />
          </div>
        ))}
      </div>
    );
  }

  // ── DESKTOP: la grilla de siempre ──
  return (
    <ReactGridLayout
      className="layout"
      layout={layout}
      cols={12}
      rowHeight={30}
      isDraggable={false}
      isResizable={false}
      margin={[12, 12]}
    >
      {layout.map((it) => (
        <div key={it.i} className="faq-canvas-item">
          <BloqueVista bloque={blocks[it.i]} onClickEnlace={onClickEnlace} />
        </div>
      ))}
    </ReactGridLayout>
  );
}